import React, { useState, useRef } from 'react';
import { Camera, Image as ImageIcon, X, Upload, Loader } from 'lucide-react';
import { storage } from '../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

interface PhotoUploadProps {
  householdId: string;
  itemId?: string; // Optional - for existing items
  currentPhotoURL?: string;
  onPhotoUploaded: (photoURL: string) => void;
  onPhotoRemoved: () => void;
}

export function PhotoUpload({
  householdId,
  itemId,
  currentPhotoURL,
  onPhotoUploaded,
  onPhotoRemoved,
}: PhotoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewURL, setPreviewURL] = useState<string | null>(currentPhotoURL || null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compress image before upload
  async function compressImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Max dimensions
          const maxWidth = 1200;
          const maxHeight = 1200;

          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.8 // 80% quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  }

  async function handleFileSelected(file: File) {
    if (!file) return;

    // Check if householdId is available
    if (!householdId) {
      setError('Please wait for household data to load');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (before compression)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      console.log('Uploading photo for household:', householdId);
      // Compress image
      const compressedBlob = await compressImage(file);

      // Generate unique filename
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const tempItemId = itemId || `temp_${timestamp}`;
      const storagePath = `itemPhotos/${householdId}/${tempItemId}/${filename}`;

      // Upload to Firebase Storage
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, compressedBlob, {
        contentType: 'image/jpeg',
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setError('Failed to upload photo. Please try again.');
          setUploading(false);
        },
        async () => {
          // Upload complete - get download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setPreviewURL(downloadURL);
          setUploading(false);
          onPhotoUploaded(downloadURL);
        }
      );
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Failed to process image');
      setUploading(false);
    }
  }

  async function handleRemovePhoto() {
    if (!previewURL) return;

    try {
      // If it's a Firebase Storage URL, delete it
      if (previewURL.includes('firebasestorage')) {
        const storageRef = ref(storage, previewURL);
        await deleteObject(storageRef);
      }

      setPreviewURL(null);
      onPhotoRemoved();
    } catch (err) {
      console.error('Error removing photo:', err);
      setError('Failed to remove photo');
    }
  }

  function handleCameraClick() {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.click();
    }
  }

  function handleGalleryClick() {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.click();
    }
  }

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelected(file);
        }}
      />

      {!previewURL && !uploading && (
        <div className="space-y-3">
          {!householdId && (
            <div className="text-center py-4">
              <Loader className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          )}
          {householdId && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleCameraClick}
                  className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-green hover:bg-primary-mint/10 transition-all"
                >
                  <Camera className="w-8 h-8 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Take Photo</span>
                </button>

                <button
                  type="button"
                  onClick={handleGalleryClick}
                  className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-green hover:bg-primary-mint/10 transition-all"
                >
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">From Gallery</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Optional - helps identify leftovers and partial items
              </p>
            </>
          )}
        </div>
      )}

      {uploading && (
        <div className="bg-light-mint p-4 rounded-xl border border-primary-green/20">
          <div className="flex items-center gap-3 mb-2">
            <Loader className="w-5 h-5 text-primary-green animate-spin" />
            <span className="text-sm font-medium text-primary-green">Uploading photo...</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-green h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-600 mt-1">{Math.round(uploadProgress)}%</p>
        </div>
      )}

      {previewURL && !uploading && (
        <div className="relative">
          <img
            src={previewURL}
            alt="Item preview"
            className="w-full h-48 object-cover rounded-xl border-2 border-gray-200"
          />
          <button
            type="button"
            onClick={handleRemovePhoto}
            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {error && (
        <div className="mt-2 bg-red-50 text-red-800 p-3 rounded-xl text-sm border border-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
